import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, CheckCircle2, Loader2, FileCheck2, AlertTriangle, Lightbulb } from "lucide-react";
import { BankVerifiedPill, checkAadhaar, checkPan } from "../../components/KycBadges";

const KYC_IDS = { aadhaar_number: { check: (v) => checkAadhaar(v), inputMode: "numeric", maxLength: 14, placeholder: "12-digit Aadhaar number" },
                  pan_number: { check: (v, name) => checkPan(v, name), inputMode: "text", maxLength: 10, placeholder: "ABCDE1234F" } };
const KycHint = ({ r, testId }) => r ? (
  <span className={`block text-[11px] mt-1 ${r.ok ? (r.warning ? "text-amber-300" : "text-emerald-300") : "text-red-300"}`} data-testid={testId}>
    {r.ok ? (r.warning ? `✓ Format valid · ${r.warning}` : "✓ Format & checksum valid") : `✘ ${r.error}`}
  </span>
) : null;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtErr = (d) => (typeof d === "string" ? d : d?.msg || "Something went wrong.");

const INPUT_CLS = "mt-1 w-full bg-nx-navy2 border border-white/10 px-3 py-2 text-sm text-white focus:border-nx-gold/60 outline-none";

const ReqBadge = ({ required, recommended }) => (
  required
    ? <span className="ml-2 text-[9px] tracking-wider uppercase px-1.5 py-0.5 bg-nx-gold/20 text-nx-goldlight border border-nx-gold/40 rounded-sm align-middle" data-testid="cdoc-badge-mandatory">Mandatory</span>
    : recommended
    ? <span className="ml-2 text-[9px] tracking-wider uppercase px-1.5 py-0.5 bg-amber-400/15 text-amber-300 border border-amber-400/40 rounded-sm align-middle" data-testid="cdoc-badge-recommended">Recommended</span>
    : <span className="ml-2 text-[9px] tracking-wider uppercase px-1.5 py-0.5 bg-white/5 text-slate-400 border border-white/10 rounded-sm align-middle" data-testid="cdoc-badge-optional">Optional</span>
);

// Current Residential Address — validity + a readable value composed into `current_residential_address`.
function computeCurrentAddress(text) {
  const t = text || {};
  const g = (k) => (t[k] || "").toString().trim();
  const mode = g("cur_addr_mode");
  const accom = g("cur_accom_type");
  if (mode === "same") {
    const perm = g("permanent_address");
    return { valid: true, composed: perm ? `Same as permanent address — ${perm}` : "Same as permanent address" };
  }
  if (mode === "different") {
    const reqOk = ["cur_addr_city", "cur_addr_district", "cur_addr_state", "cur_addr_pincode"].every((k) => g(k));
    let valid = reqOk && !!accom;
    if (accom === "PG") valid = valid && !!g("cur_warden_phone");
    if (accom === "Rental") valid = valid && !!g("cur_owner_phone");
    let composed = "";
    if (valid) {
      const line = [g("cur_addr_line1"), g("cur_addr_line2")].filter(Boolean).join(", ");
      const parts = [line, g("cur_addr_city"), g("cur_addr_district"), g("cur_addr_state")].filter(Boolean).join(", ");
      composed = `${parts} - ${g("cur_addr_pincode")} | Accommodation: ${accom}`;
      if (accom === "PG") composed += ` | Warden Ph: ${g("cur_warden_phone")}`;
      if (accom === "Rental") composed += ` | Owner Ph: ${g("cur_owner_phone")}`;
    }
    return { valid, composed };
  }
  return { valid: false, composed: "" };
}

const Ring = ({ pct }) => {
  const r = 11, c = 2 * Math.PI * r;
  return (
    <svg width="30" height="30" viewBox="0 0 30 30" className="shrink-0" data-testid="cdoc-progress-ring">
      <circle cx="15" cy="15" r={r} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="3" />
      <circle cx="15" cy="15" r={r} fill="none" stroke="#bd8931" strokeWidth="3" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c * (1 - pct / 100)} transform="rotate(-90 15 15)"
        style={{ transition: "stroke-dashoffset 0.2s ease" }} />
      <text x="15" y="15" textAnchor="middle" dominantBaseline="central" fontSize="8" fill="#fff" fontWeight="600">{pct}</text>
    </svg>
  );
};

const FileItem = ({ token, item, state, onDone, recommended, highlight, locked = false }) => {
  const ref = useRef(null);
  const lastFileRef = useRef(null);
  const [phase, setPhase] = useState("idle"); // idle | uploading | verifying | done | error
  const [progress, setProgress] = useState(0);
  const done = !!state?.uploaded_at;
  const showDone = phase === "done" || (done && phase === "idle");

  const upload = async (f) => {
    if (!f) return;
    lastFileRef.current = f;
    setProgress(0);
    setPhase("uploading");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await axios.post(`${API}/candidate-docs/token/${token}/upload/${item.id}`, fd, {
        onUploadProgress: (e) => {
          if (!e.total) return;
          const pct = Math.round((e.loaded * 100) / e.total);
          setProgress(pct);
          if (pct >= 100) setPhase((p) => (p === "uploading" ? "verifying" : p));
        },
      });
      setPhase("done");
      toast.success(`${item.label} uploaded${data?.match?.detected ? ` (verified: ${data.match.detected})` : ""}`);
      onDone();
    } catch (err) {
      setPhase("error");
      toast.error(fmtErr(err?.response?.data?.detail));
    }
  };

  return (
    <div id={`doc-row-${item.id}`} className={`flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-3 border bg-nx-navy2/40 px-3 py-2.5 transition-all duration-500 ${highlight ? "ring-2 ring-amber-400 border-amber-400/60" : "border-white/10"}`} data-testid={`cdoc-file-${item.id}`}>
      <div className="min-w-0 w-full sm:w-auto">
        <p className="text-sm text-white sm:truncate">{item.label}<ReqBadge required={item.required} recommended={recommended} /></p>
        {showDone && <p className="text-[11px] text-emerald-400 flex items-center gap-1 mt-0.5 break-all"><FileCheck2 size={11} className="shrink-0" /> {state?.name?.slice(0, 34) || "Uploaded"}</p>}
      </div>

      {phase === "uploading" ? (
        <div className="shrink-0 flex items-center gap-2" data-testid={`cdoc-uploading-${item.id}`}>
          <Ring pct={progress} />
          <span className="text-[11px] text-nx-goldlight">Uploading {progress}%</span>
        </div>
      ) : phase === "verifying" ? (
        <div className="shrink-0 flex items-center gap-1.5 text-[11px] text-amber-300" data-testid={`cdoc-verifying-${item.id}`}>
          <Loader2 size={14} className="animate-spin" /> Verifying…
        </div>
      ) : phase === "error" ? (
        <div className="shrink-0 flex items-center gap-2" data-testid={`cdoc-failed-${item.id}`}>
          <span className="flex items-center gap-1 text-[11px] text-red-400"><AlertTriangle size={13} /> Upload Failed</span>
          <button type="button" onClick={() => upload(lastFileRef.current)}
            className="text-[11px] uppercase tracking-wide px-2.5 py-1.5 border border-red-400/50 text-red-300 hover:bg-red-400/10 transition-colors"
            data-testid={`cdoc-retry-${item.id}`}>Retry</button>
        </div>
      ) : showDone ? (
        <div className="shrink-0 flex items-center gap-2">
          {state?.rejected_at ? (
            <>
              <span className="flex flex-col text-[11px] text-red-300" data-testid={`cdoc-rejected-${item.id}`}><span className="flex items-center gap-1"><AlertTriangle size={13} /> Rejected by HR</span>{state.rejected_reason && <span className="text-slate-400 font-normal">{state.rejected_reason}</span>}</span>
              <button type="button" onClick={() => ref.current?.click()}
                className="text-[11px] uppercase tracking-wide px-2.5 py-1.5 border border-red-400/60 text-red-200 hover:bg-red-400/10 transition-colors"
                data-testid={`cdoc-replace-${item.id}`}>Re-upload</button>
            </>
          ) : state?.replacement_requested_at ? (
            <>
              <span className="flex items-center gap-1 text-[11px] text-amber-300" data-testid={`cdoc-replace-requested-${item.id}`}><AlertTriangle size={13} /> Replacement requested</span>
              <button type="button" onClick={() => ref.current?.click()}
                className="text-[11px] uppercase tracking-wide px-2.5 py-1.5 border border-amber-400/60 text-amber-200 hover:bg-amber-400/10 transition-colors"
                data-testid={`cdoc-replace-${item.id}`}>Re-upload</button>
            </>
          ) : (
            <span className="flex items-center gap-1 text-[11px] text-emerald-300" data-testid={`cdoc-uploaded-${item.id}`}><CheckCircle2 size={13} /> {state?.verified ? "Verified by HR" : "Uploaded"}</span>
          )}
          {!state?.replacement_requested_at && !state?.rejected_at && !state?.verified && !locked && (
            <button type="button" onClick={() => ref.current?.click()}
              className="text-[11px] uppercase tracking-wide px-2.5 py-1.5 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 transition-colors"
              data-testid={`cdoc-replace-${item.id}`}>Replace</button>
          )}
        </div>
      ) : (
        <button type="button" onClick={() => ref.current?.click()}
          className="shrink-0 flex items-center gap-1.5 text-[11px] tracking-wide uppercase px-3 py-2 border border-nx-gold/50 text-nx-goldlight hover:bg-nx-gold/10 transition-colors"
          data-testid={`cdoc-upload-${item.id}`}>
          <Upload size={12} /> Upload
        </button>
      )}

      <input ref={ref} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden"
        onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
    </div>
  );
};

export default function CandidateDocs({ token, onSubmitted, resubmitOnly = false }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [text, setText] = useState({});
  const [acks, setAcks] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [accBusy, setAccBusy] = useState(false);
  const [textBusy, setTextBusy] = useState("");
  const resubmitText = async (itemId) => {
    setTextBusy(itemId);
    try {
      await axios.post(`${API}/candidate-docs/token/${token}/text/${itemId}`, { value: text[itemId] || "" });
      toast.success("Information resubmitted — thank you!");
      await load();
    } catch (err) {
      toast.error(fmtErr(err?.response?.data?.detail));
    } finally { setTextBusy(""); }
  };
  const reuploadAcceptance = async (file) => {
    if (!file) return;
    setAccBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/accept/${token}/upload`, fd);
      toast.success("Signed letter re-uploaded — thank you!");
      await load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Upload failed. Please try again.");
    } finally { setAccBusy(false); }
  };
  const [highlightId, setHighlightId] = useState(null);

  const scrollToDoc = (id) => {
    const el = document.getElementById(`doc-row-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightId(id);
    setTimeout(() => setHighlightId((h) => (h === id ? null : h)), 2200);
  };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/candidate-docs/token/${token}`);
      setData(data);
      setText(data.text || {});
      setAcks(data.acks || {});
      if (data.status === "submitted" && onSubmitted) onSubmitted();
    } catch (e) {
      setErr(fmtErr(e?.response?.data?.detail) || e?.message || "Could not load the document form.");
    }
  }, [token, onSubmitted]);

  useEffect(() => { load(); }, [load]);

  // Keep the composed current-address value in sync with the structured sub-fields.
  useEffect(() => {
    const { composed } = computeCurrentAddress(text);
    setText((t) => (t.current_residential_address === composed ? t : { ...t, current_residential_address: composed }));
  }, [text]);

  if (err) return <p className="text-red-400 text-sm text-center py-6" data-testid="cdoc-error">{err}</p>;
  if (!data) return <p className="text-slate-400 text-sm text-center py-6" data-testid="cdoc-loading">Loading document form…</p>;

  const ackItems = data.items.filter((i) => i.type === "ack");
  const allAcked = ackItems.every((a) => acks[a.id]);
  const addrRequired = data.items.some((i) => i.type === "text" && i.id === "current_residential_address");
  const { valid: addrValid } = computeCurrentAddress(text);
  const addrMode = (text.cur_addr_mode || "").toString();
  const accomType = (text.cur_accom_type || "").toString();
  const setField = (id, v) => setText((t) => ({ ...t, [id]: v }));

  // Group every catalog item under its category, preserving order (all 9 categories shown).
  const folders = [];
  for (const it of data.items) {
    let g = folders.find((f) => f.folder === it.folder);
    if (!g) { g = { folder: it.folder, items: [] }; folders.push(g); }
    g.items.push(it);
  }
  const suggestedIds = new Set((data.suggested?.documents || []).map((s) => s.id));

  // Mandatory-documents progress (files + text details required for this candidate; excludes declarations).
  const mandatoryItems = data.items.filter((i) => i.required && (i.type === "file" || i.type === "text"));
  const isItemDone = (i) => (i.type === "file" ? !!data.files?.[i.id]?.uploaded_at : (text[i.id] || "").toString().trim() !== "");
  const mandTotal = mandatoryItems.length;
  const mandDone = mandatoryItems.filter(isItemDone).length;
  const mandPct = mandTotal ? Math.round((mandDone / mandTotal) * 100) : 100;
  const mandComplete = mandTotal > 0 && mandDone === mandTotal;

  const renderAddressBlock = () => (
    <div data-testid="cdoc-current-address">
      <label className="block mb-3">
        <span className="text-[11px] text-slate-400">Is your current address the same as your permanent address?<span className="text-nx-goldlight"> *</span></span>
        <select value={addrMode} onChange={(e) => setField("cur_addr_mode", e.target.value)} className={INPUT_CLS} data-testid="cdoc-addr-mode">
          <option value="">Select…</option>
          <option value="same">Same as Permanent Address</option>
          <option value="different">Different from Permanent Address</option>
        </select>
      </label>

      {addrMode === "same" && (
        <div className="text-xs text-slate-300 border border-white/10 bg-nx-navy2/40 px-3 py-2.5 mb-3" data-testid="cdoc-addr-same-note">
          Your current address will be recorded as the same as your permanent address:
          <span className="block mt-1 text-white">{(text.permanent_address || "").trim() || "Please enter your permanent address details above."}</span>
        </div>
      )}

      {addrMode === "different" && (
        <div className="mb-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block sm:col-span-2">
              <span className="text-[11px] text-slate-400">Address Line 1</span>
              <input value={text.cur_addr_line1 || ""} onChange={(e) => setField("cur_addr_line1", e.target.value)} className={INPUT_CLS} data-testid="cdoc-addr-line1" />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-[11px] text-slate-400">Address Line 2 (optional)</span>
              <input value={text.cur_addr_line2 || ""} onChange={(e) => setField("cur_addr_line2", e.target.value)} className={INPUT_CLS} data-testid="cdoc-addr-line2" />
            </label>
            <label className="block">
              <span className="text-[11px] text-slate-400">City<span className="text-nx-goldlight"> *</span></span>
              <input value={text.cur_addr_city || ""} onChange={(e) => setField("cur_addr_city", e.target.value)} className={INPUT_CLS} data-testid="cdoc-addr-city" />
            </label>
            <label className="block">
              <span className="text-[11px] text-slate-400">District<span className="text-nx-goldlight"> *</span></span>
              <input value={text.cur_addr_district || ""} onChange={(e) => setField("cur_addr_district", e.target.value)} className={INPUT_CLS} data-testid="cdoc-addr-district" />
            </label>
            <label className="block">
              <span className="text-[11px] text-slate-400">State<span className="text-nx-goldlight"> *</span></span>
              <input value={text.cur_addr_state || ""} onChange={(e) => setField("cur_addr_state", e.target.value)} className={INPUT_CLS} data-testid="cdoc-addr-state" />
            </label>
            <label className="block">
              <span className="text-[11px] text-slate-400">Pincode<span className="text-nx-goldlight"> *</span></span>
              <input value={text.cur_addr_pincode || ""} inputMode="numeric" onChange={(e) => setField("cur_addr_pincode", e.target.value)} className={INPUT_CLS} data-testid="cdoc-addr-pincode" />
            </label>
          </div>

          <label className="block mt-3">
            <span className="text-[11px] text-slate-400">Accommodation type<span className="text-nx-goldlight"> *</span></span>
            <select value={accomType} onChange={(e) => setField("cur_accom_type", e.target.value)} className={INPUT_CLS} data-testid="cdoc-accom-type">
              <option value="">Select…</option>
              <option value="PG">PG</option>
              <option value="Rental">Rental</option>
              <option value="Owned">Owned</option>
            </select>
          </label>

          {accomType === "PG" && (
            <label className="block mt-3">
              <span className="text-[11px] text-slate-400">Warden Phone Number<span className="text-nx-goldlight"> *</span></span>
              <input value={text.cur_warden_phone || ""} inputMode="tel" onChange={(e) => setField("cur_warden_phone", e.target.value)} className={INPUT_CLS} data-testid="cdoc-warden-phone" />
            </label>
          )}
          {accomType === "Rental" && (
            <label className="block mt-3">
              <span className="text-[11px] text-slate-400">Owner Phone Number<span className="text-nx-goldlight"> *</span></span>
              <input value={text.cur_owner_phone || ""} inputMode="tel" onChange={(e) => setField("cur_owner_phone", e.target.value)} className={INPUT_CLS} data-testid="cdoc-owner-phone" />
            </label>
          )}
        </div>
      )}

      <div className="border border-red-500/50 bg-red-500/10 px-4 py-2.5 mt-1 text-xs text-red-200 font-semibold flex items-start gap-2" data-testid="cdoc-addr-legal">
        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
        <span>Providing false or incorrect address information will result in immediate disqualification of the appointment offer and may lead to further legal action.</span>
      </div>
    </div>
  );

  const renderItem = (it) => {
    const rec = !it.required && suggestedIds.has(it.id);
    if (it.type === "file") return <FileItem key={it.id} token={token} item={it} state={data.files?.[it.id]} onDone={load} recommended={rec} highlight={highlightId === it.id} />;
    if (it.type === "acceptance") {
      const acc = data.acceptance || {};
      const redo = acc.rejected_at || acc.replacement_requested_at;
      return (
        <div key={it.id} className={`flex items-center justify-between gap-3 border px-3 py-2.5 ${redo ? "border-amber-500/40 bg-amber-500/5" : "border-emerald-500/30 bg-emerald-500/5"}`} data-testid={`cdoc-acceptance-${it.id}`}>
          <div className="min-w-0">
            <p className="text-sm text-white">{it.label}<ReqBadge required /></p>
            {acc.rejected_at && <p className="text-[11px] text-red-300 mt-0.5">Rejected by HR{acc.rejected_reason ? ` — ${acc.rejected_reason}` : ""}. Please re-upload the signed letter.</p>}
            {!acc.rejected_at && acc.replacement_requested_at && <p className="text-[11px] text-amber-300 mt-0.5">HR has requested a replacement — please re-upload the signed letter.</p>}
          </div>
          {redo ? (
            <label className={`text-[11px] uppercase tracking-wide px-2.5 py-1.5 border cursor-pointer shrink-0 ${acc.rejected_at ? "border-red-400/60 text-red-200 hover:bg-red-400/10" : "border-amber-400/60 text-amber-200 hover:bg-amber-400/10"}`} data-testid="cdoc-acceptance-reupload">
              {accBusy ? "Uploading…" : "Re-upload"}
              <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" disabled={accBusy} onChange={(e) => { reuploadAcceptance(e.target.files?.[0]); e.target.value = ""; }} />
            </label>
          ) : (
            <span className="text-[11px] text-emerald-300 flex items-center gap-1 shrink-0"><CheckCircle2 size={12} /> Already submitted</span>
          )}
        </div>
      );
    }
    if (it.type === "ack") return (
      <label key={it.id} className="flex items-start gap-2.5 text-sm text-slate-300 cursor-pointer border border-white/10 bg-nx-navy2/40 px-3 py-2.5" data-testid={`cdoc-ack-${it.id}`}>
        <input type="checkbox" checked={!!acks[it.id]} onChange={(e) => setAcks((a) => ({ ...a, [it.id]: e.target.checked }))}
          className="mt-0.5 accent-nx-gold w-4 h-4 shrink-0" data-testid={`cdoc-ack-input-${it.id}`} />
        <span>{it.statement ? it.statement : <>I have read and agree to the <b className="text-white">{it.label}</b>.</>}<ReqBadge required /></span>
      </label>
    );
    if (it.id === "current_residential_address") return (
      <div key={it.id} className="border border-white/10 bg-nx-navy2/40 px-3 py-3">
        <p className="text-sm text-white mb-2">{it.label}<ReqBadge required={it.required} /></p>
        {renderAddressBlock()}
      </div>
    );
    if (it.type === "text" && data.text_rejected?.[it.id]) {
      const rj = data.text_rejected[it.id];
      return (
        <div key={it.id} id={`doc-row-${it.id}`} className="border border-red-500/40 bg-red-500/5 px-3 py-2.5" data-testid={`cdoc-text-rejected-${it.id}`}>
          <p className="text-sm text-white">{it.label}<ReqBadge required={it.required} /></p>
          <p className="text-[11px] text-red-300 mt-1">Rejected by HR{rj.reason ? ` — ${rj.reason}` : ""}. Please enter the correct information.</p>
          <form onSubmit={(e) => { e.preventDefault(); resubmitText(it.id); }} className="flex flex-col sm:flex-row gap-2 mt-2">
            <input value={text[it.id] || ""} onChange={(e) => setText((t) => ({ ...t, [it.id]: e.target.value }))} className={INPUT_CLS + " flex-1"} data-testid={`cdoc-text-${it.id}`} />
            <button type="submit" disabled={textBusy === it.id} className="text-[11px] uppercase tracking-wide px-4 py-2.5 border border-red-400/60 text-red-200 hover:bg-red-400/10 transition-colors disabled:opacity-60 shrink-0" data-testid={`cdoc-text-resubmit-${it.id}`}>{textBusy === it.id ? "Saving…" : "Resubmit"}</button>
          </form>
          {KYC_IDS[it.id] && <KycHint r={KYC_IDS[it.id].check(text[it.id], data.employee_name)} testId={`cdoc-kyc-hint-${it.id}`} />}
        </div>
      );
    }
    const kycCfg = KYC_IDS[it.id];
    const kycRes = kycCfg ? kycCfg.check(text[it.id], data.employee_name) : null;
    return (
      <label key={it.id} id={`doc-row-${it.id}`} className={`block border bg-nx-navy2/40 px-3 py-2.5 transition-all duration-500 ${highlightId === it.id ? "ring-2 ring-amber-400 border-amber-400/60" : kycRes && !kycRes.ok ? "border-red-500/50" : "border-white/10"}`}>
        <span className="text-sm text-white">{it.label}<ReqBadge required={it.required} recommended={rec} /></span>
        <input value={text[it.id] || ""} onChange={(e) => setText((t) => ({ ...t, [it.id]: kycCfg && it.id === "pan_number" ? e.target.value.toUpperCase() : e.target.value }))}
          inputMode={kycCfg?.inputMode} maxLength={kycCfg?.maxLength} placeholder={kycCfg?.placeholder} autoComplete="off"
          className={INPUT_CLS} data-testid={`cdoc-text-${it.id}`} />
        <KycHint r={kycRes} testId={`cdoc-kyc-hint-${it.id}`} />
      </label>
    );
  };

  const kycInvalid = Object.keys(KYC_IDS).some((id) => { const r = KYC_IDS[id].check(text[id], data.employee_name); return r && !r.ok; });
  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/candidate-docs/token/${token}/submit`, { text, acks });
      toast.success("Documents submitted — thank you!");
      const { data: fresh } = await axios.get(`${API}/candidate-docs/token/${token}`);
      setData(fresh);
      if (onSubmitted) onSubmitted();
    } catch (err) {
      toast.error(fmtErr(err?.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const redoItems = data.items.filter((it) => (it.type === "file" && (data.files?.[it.id]?.replacement_requested_at || data.files?.[it.id]?.rejected_at))
    || (it.type === "text" && data.text_rejected?.[it.id])
    || (it.type === "acceptance" && (data.acceptance?.replacement_requested_at || data.acceptance?.rejected_at)));

  if (resubmitOnly) {
    return (
      <div data-testid="cdoc-resubmit">
        <p className="text-[10px] tracking-[0.26em] uppercase text-nx-goldlight">Document resubmission</p>
        <h1 className="text-white text-lg mt-1">Hello {data.employee_name.split(" ")[0]},</h1>
        {redoItems.length === 0 ? (
          <div className="mt-5 border border-emerald-500/30 bg-emerald-500/5 p-4" data-testid="cdoc-resubmit-done">
            <p className="text-sm text-emerald-300 flex items-center gap-2"><CheckCircle2 size={16} /> Nothing pending — every requested item has been resubmitted. Thank you.</p>
            <p className="text-xs text-slate-500 mt-2">HR will verify your documents and contact you if anything else is needed.</p>
          </div>
        ) : (
          <div className="mt-5 border border-amber-500/40 bg-amber-500/5 p-4" data-testid="cdoc-resubmit-section">
            <p className="text-xs text-amber-200 mb-3 flex items-center gap-2"><AlertTriangle size={14} /> HR has asked you to resubmit the {redoItems.length > 1 ? `${redoItems.length} items` : "item"} below. Only {redoItems.length > 1 ? "these" : "this"} can be changed — everything else is already on file.</p>
            <div className="space-y-2">
              {redoItems.map((it) => it.type === "file" ? <FileItem key={it.id} token={token} item={it} state={data.files?.[it.id]} onDone={load} recommended={false} highlight={false} /> : renderItem(it))}
            </div>
          </div>
        )}
        <p className="text-[11px] text-slate-600 mt-5">Questions? Contact hr@nexsecure.in</p>
      </div>
    );
  }

  if (data.status === "submitted") {
    const redo = redoItems;
    return (
      <div className="text-center" data-testid="cdoc-submitted">
        <CheckCircle2 className="mx-auto text-emerald-400 mb-3" size={32} />
        <h1 className="text-white text-lg">Documents submitted</h1>
        <p className="text-slate-400 text-sm mt-2">Thank you. Our HR team will verify your documents and be in touch with your onboarding details.</p>
        {data.bank_verified?.at && <div className="mt-4 flex justify-center"><BankVerifiedPill v={data.bank_verified} testId="cdoc-bank-verified-pill" /></div>}
        {redo.length > 0 && (
          <div className="mt-6 text-left border border-amber-500/40 bg-amber-500/5 p-4" data-testid="cdoc-replace-section">
            <p className="text-xs text-amber-200 mb-3 flex items-center gap-2"><AlertTriangle size={14} /> HR has asked you to re-upload the document{redo.length > 1 ? "s" : ""} below. Only {redo.length > 1 ? "these" : "this"} can be changed.</p>
            <div className="space-y-2">
              {redo.map((it) => it.type === "file" ? <FileItem key={it.id} token={token} item={it} state={data.files?.[it.id]} onDone={load} recommended={false} highlight={false} /> : renderItem(it))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="cdoc-form">
      <div className="flex items-center gap-2 mb-1">
        <FileCheck2 size={18} className="text-nx-goldlight" />
        <h1 className="text-white text-lg">Document Submission</h1>
      </div>
      <p className="text-slate-400 text-sm mb-4">
        Your signed acceptance was received. Please submit the documents below to continue onboarding.
        {data.exempt ? " For your role the educational certificate (10th) is optional — all other documents and details below are required." : " All items marked * are required."}
      </p>
      <div className="border border-red-500/50 bg-red-500/10 px-4 py-2.5 mb-5 text-xs text-red-300 flex items-start gap-2" data-testid="cdoc-fraud-warning">
        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
        <span>Upload <b>clear scans or photographs of your ORIGINAL documents</b>. Each upload is automatically checked against the requested document. Submitting <b>fake, edited or incorrect documents</b> will result in <b>immediate disqualification and permanent blacklisting</b> from NexSecure Technologies and its associated companies.</span>
      </div>

      <div className={`border px-4 py-3 mb-5 transition-colors duration-500 ${mandComplete ? "border-emerald-500/50 bg-emerald-500/10" : "border-white/10 bg-nx-navy2/40"}`} data-testid="cdoc-progress">
        <div className="flex items-center justify-between mb-2">
          <span className={`text-xs font-semibold ${mandComplete ? "text-emerald-300" : "text-white"}`} data-testid="cdoc-progress-count">
            {mandDone} of {mandTotal} mandatory documents uploaded
          </span>
          <span className={`text-xs font-semibold ${mandComplete ? "text-emerald-300" : "text-nx-goldlight"}`}>{mandPct}%</span>
        </div>
        <div className="h-2 w-full rounded-full bg-white/10 overflow-hidden">
          <div className={`h-full rounded-full transition-all duration-500 ease-out ${mandComplete ? "bg-emerald-400" : "bg-nx-gold"}`} style={{ width: `${mandPct}%` }} data-testid="cdoc-progress-fill" />
        </div>
        {mandComplete && (
          <p className="text-[11px] text-emerald-300 flex items-center gap-1.5 mt-2" data-testid="cdoc-progress-complete">
            <CheckCircle2 size={13} className="shrink-0" /> All mandatory documents uploaded. Please proceed to the declarations section.
          </p>
        )}
      </div>

      <p className="text-[11px] text-slate-500 mb-4">Below is your complete document checklist across all 9 categories. Every item is visible. <span className="text-nx-goldlight font-semibold">Mandatory</span> items must be completed; <span className="text-slate-400">Optional</span> items can be uploaded if relevant to your qualifications or role. Files: PDF / JPG / PNG ≤10MB.</p>

      {data.suggested?.message && (
        <div className="border border-amber-400/40 bg-amber-400/[0.06] px-4 py-3 mb-5 text-xs text-amber-100" data-testid="cdoc-nudge">
          <p className="font-semibold text-amber-300 flex items-center gap-1.5 mb-1"><Lightbulb size={14} className="shrink-0" /> A friendly suggestion for your role{data.designation ? ` — ${data.designation}` : ""}</p>
          <p className="text-amber-100/85 leading-relaxed">{data.suggested.message}</p>
          {data.suggested.documents?.length > 0 && (
            <ul className="mt-2 space-y-1" data-testid="cdoc-nudge-list">
              {data.suggested.documents.map((s) => (
                <li key={s.id} className="flex items-start gap-1.5">
                  <span className="text-amber-300 mt-0.5">•</span>
                  <button type="button" onClick={() => scrollToDoc(s.id)}
                    className="text-left text-white underline decoration-amber-400/50 underline-offset-2 hover:text-amber-200 hover:decoration-amber-300 transition-colors"
                    data-testid={`cdoc-nudge-link-${s.id}`}>{s.label}</button>
                </li>
              ))}
            </ul>
          )}
          <p className="text-amber-100/60 mt-2 italic">These are optional — upload the ones you have, or skip them. They can help speed up your verification.</p>
        </div>
      )}

      {folders.map((g) => (
        <div key={g.folder} className="mb-6" data-testid={`cdoc-folder-${g.folder.slice(0, 2)}`}>
          <p className="text-[11px] tracking-[0.18em] uppercase text-nx-goldlight font-semibold mb-2 border-b border-nx-gold/20 pb-1.5">{g.folder}</p>
          <div className="space-y-2">
            {g.items.map((it) => renderItem(it))}
          </div>
        </div>
      ))}

      <button onClick={submit} disabled={submitting || !allAcked || (addrRequired && !addrValid) || kycInvalid}
        className="w-full flex items-center justify-center gap-2 text-xs tracking-[0.16em] uppercase px-6 py-3.5 bg-nx-gold text-nx-navy font-semibold hover:brightness-110 transition-all disabled:opacity-50"
        data-testid="cdoc-submit-btn">
        {submitting ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Submit Documents
      </button>
      {!allAcked && <p className="text-[11px] text-slate-500 text-center mt-2">Tick all declarations to enable submission.</p>}
      {allAcked && addrRequired && !addrValid && <p className="text-[11px] text-slate-500 text-center mt-2">Complete the Current Residential Address section to enable submission.</p>}
      {kycInvalid && <p className="text-[11px] text-red-300 text-center mt-2" data-testid="cdoc-kyc-block">Fix the Aadhaar / PAN number highlighted in red to enable submission.</p>}
    </div>
  );
}
